import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '../context/ToastContext.jsx';

/**
 * Fetches on mount and whenever `deps` change, with the three states every
 * screen needs (loading / error / data) and a `reload` for after a mutation.
 *
 *   const { data, loading, error, reload } = useApi(() => employeeApi.list({ page }), [page]);
 */
export function useApi(fetcher, deps = [], { immediate = true, onSuccess } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(immediate);
  const [error, setError] = useState(null);

  // Guards against setting state after unmount, and against a slow earlier
  // request overwriting a faster later one when filters change quickly.
  const requestId = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);

    try {
      const result = await fetcher();
      if (!mounted.current || id !== requestId.current) return null;
      setData(result);
      onSuccess?.(result);
      return result;
    } catch (err) {
      if (!mounted.current || id !== requestId.current) return null;
      setError(err);
      return null;
    } finally {
      if (mounted.current && id === requestId.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    if (immediate) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, immediate]);

  return { data, loading, error, reload: run, setData };
}

/**
 * Wraps a mutation with a pending flag and toast feedback, so screens do not
 * repeat the same try/catch/toast block for every button.
 */
export function useMutation(mutator, { successMessage, onSuccess, onError } = {}) {
  const toast = useToast();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);

  const mutate = useCallback(
    async (...args) => {
      setPending(true);
      setError(null);
      try {
        const result = await mutator(...args);
        if (successMessage) toast.success(typeof successMessage === 'function' ? successMessage(result) : successMessage);
        onSuccess?.(result);
        return result;
      } catch (err) {
        setError(err);
        // Validation errors are shown against the fields, not as a toast.
        if (err.code !== 'VALIDATION_ERROR') toast.error(err.message);
        onError?.(err);
        return null;
      } finally {
        setPending(false);
      }
    },
    [mutator, successMessage, onSuccess, onError, toast]
  );

  return { mutate, pending, error, fieldErrors: error?.fieldErrors ?? {} };
}

/** Delays a fast-changing value - used for search boxes. */
export function useDebounced(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

/** Page state plus the handler a list screen needs. */
export function usePagination(initialLimit = 25) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(initialLimit);

  return {
    page,
    limit,
    setPage,
    setLimit,
    /** Filters changing must send the user back to page 1. */
    reset: () => setPage(1),
  };
}
